import { getApiUrl } from "../config/api";

/**
 * Admin User Management Service
 * All endpoints require admin privileges
 */

/**
 * Get all users with pagination and filters
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.pageSize - Items per page (default: 50)
 * @param {string} params.role - Filter by role: "dealer" or "client"
 * @param {string} params.search - Search by name or email
 * @returns {Promise<Object>} Paginated users response
 */
export const getAllUsers = async ({ page = 1, pageSize = 50, role, search } = {}) => {
  const params = new URLSearchParams();
  params.append('page', page);
  params.append('pageSize', pageSize);
  if (role) params.append('role', role);
  if (search) params.append('search', search);

  const response = await fetch(getApiUrl(`/api/admin/users?${params.toString()}`));
  
  if (!response.ok) {
    throw new Error(`Failed to fetch users: ${response.status}`);
  }
  
  return response.json();
};

/**
 * Get user by ID
 * @param {number} userId - User ID
 * @returns {Promise<Object>} User data
 */
export const getUserById = async (userId) => {
  const response = await fetch(getApiUrl(`/api/admin/users/${userId}`));
  
  if (!response.ok) {
    throw new Error(`Failed to fetch user: ${response.status}`);
  }
  
  return response.json();
};

/**
 * Update user information
 * @param {number} userId - User ID
 * @param {Object} userData - User data to update
 * @returns {Promise<Object>} Updated user data
 */
export const updateUser = async (userId, userData) => {
  const response = await fetch(getApiUrl(`/api/admin/users/${userId}`), {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(userData)
  });
  
  if (!response.ok) {
    throw new Error(`Failed to update user: ${response.status}`);
  }
  
  return response.json();
};

/**
 * Enable user account
 * @param {number} userId - User ID
 * @returns {Promise<Object>} Updated user data
 */
export const enableUser = async (userId) => {
  const response = await fetch(getApiUrl(`/api/admin/users/${userId}/enable`), {
    method: 'POST'
  });
  
  if (!response.ok) {
    throw new Error(`Failed to enable user: ${response.status}`);
  }
  
  return response.json();
};

/**
 * Disable user account
 * @param {number} userId - User ID
 * @returns {Promise<Object>} Updated user data
 */
export const disableUser = async (userId) => {
  const response = await fetch(getApiUrl(`/api/admin/users/${userId}/disable`), {
    method: 'POST'
  });
  
  if (!response.ok) {
    throw new Error(`Failed to disable user: ${response.status}`);
  }
  
  return response.json();
};

/**
 * Set admin status for user
 * @param {number} userId - User ID
 * @param {boolean} isAdmin - Whether user should be admin
 * @returns {Promise<Object>} Updated user data
 */
export const setAdminStatus = async (userId, isAdmin) => {
  const response = await fetch(getApiUrl(`/api/admin/users/${userId}/admin`), {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ isAdmin })
  });
  
  if (!response.ok) {
    throw new Error(`Failed to set admin status: ${response.status}`);
  }
  
  return response.json();
};

/**
 * Delete user (soft delete)
 * @param {number} userId - User ID
 * @returns {Promise<void>}
 */
export const deleteUser = async (userId) => {
  const response = await fetch(getApiUrl(`/api/admin/users/${userId}`), {
    method: 'DELETE'
  });
  
  if (!response.ok && response.status !== 204) {
    throw new Error(`Failed to delete user: ${response.status}`);
  }
};

/**
 * Get all admin users
 * @returns {Promise<Array>} Array of admin users
 */
export const getAllAdmins = async () => {
  const response = await fetch(getApiUrl('/api/admin/users/admins'));
  
  if (!response.ok) {
    throw new Error(`Failed to fetch admins: ${response.status}`);
  }
  
  return response.json();
};

/**
 * Get all disabled users
 * @returns {Promise<Array>} Array of disabled users
 */
export const getDisabledUsers = async () => {
  const response = await fetch(getApiUrl('/api/admin/users/disabled'));
  
  if (!response.ok) {
    throw new Error(`Failed to fetch disabled users: ${response.status}`);
  }
  
  return response.json();
};
